
import { EquipmentFitments } from "@/api";
import {
  EquipmentFitment,
  EquipmentFitmentLine,
  EquipmentFitmentStage,
  EquipmentFitmentStageKey
} from "@/api/equipmentFitments";
import TitleButtons from "@/components/common/TitleButtons.vue";
import EquipmentFitmentItemsTable from "@/components/equipmentFitment/EquipmentFitmentItemsTable.vue";
import moment from "moment";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

const STAGE_KEYS: EquipmentFitmentStageKey[] = [
  "proforma",
  "revised",
  "invoice"
];

interface InvoiceForm {
  reference: string | null;
  total: number | null;
}

const emptyDates = (): Record<EquipmentFitmentStageKey, string | null> => ({
  proforma: null,
  revised: null,
  invoice: null
});

/**
 * The fitment as a ticket: its details, the items being quoted (revised here
 * until the order and invoice numbers are in), the proforma · revised quote ·
 * invoice it goes through, and who did what to it along the way. Everything
 * on it is saved by the one Save button. Opened from the list's View button;
 * the general details are edited on the edit page.
 */
@Component({ components: { EquipmentFitmentItemsTable, TitleButtons } })
export default class EquipmentFitmentViewDialog extends Vue {
  @Prop({ default: false })
  value!: boolean;

  @Prop({ default: null })
  fitmentId!: number | null;

  fitment: EquipmentFitment | null = null;
  lines: EquipmentFitmentLine[] = [];
  notes: string | null = null;
  /** The revised quote's reference. */
  poNumber: string | null = null;
  dates = emptyDates();
  invoice: InvoiceForm = { reference: null, total: null };
  /** Field errors the server sent back for the last save. */
  fieldErrors: Record<string, string[]> = {};
  dateMenus: Record<EquipmentFitmentStageKey, boolean> = {
    proforma: false,
    revised: false,
    invoice: false
  };
  loading = false;
  /** Which action is in flight, so every button waits for it. */
  busy: string | null = null;
  /** Whether anything was saved, so the list behind refreshes on close. */
  changed = false;

  get isCashSale(): boolean {
    return Boolean(this.fitment && this.fitment.cashSale);
  }

  /** The columns whose value is derived from the items rather than typed. */
  get derivedValueStages(): EquipmentFitmentStageKey[] {
    return this.isCashSale ? ["proforma"] : ["proforma", "revised"];
  }

  /** A cash sale has no revised quote, so that column is not shown. */
  get stageKeys(): EquipmentFitmentStageKey[] {
    return this.fitment && this.fitment.cashSale
      ? STAGE_KEYS.filter(stage => stage !== "revised")
      : STAGE_KEYS;
  }
  stageLabels: Record<EquipmentFitmentStageKey, string> = {
    proforma: "Proforma",
    revised: "Revised quote",
    invoice: "Invoice"
  };
  /** Keyed by the server's action in camel case, as the linter wants it. */
  actionLabels: Record<string, string> = {
    created: "Created",
    updated: "Edited",
    itemsRevised: "Items revised",
    stageIssued: "Stage added",
    stageUpdated: "Stage updated",
    documentUploaded: "Document uploaded",
    approved: "Approved",
    // The stored status and action stay "Closed"; the screen says Finalized.
    closed: "Finalized"
  };

  @Watch("value")
  async onOpen(open: boolean): Promise<void> {
    if (open) {
      await this.load();
    }
  }

  async load(): Promise<void> {
    if (!this.fitmentId) return;
    this.loading = true;
    try {
      this.apply(await EquipmentFitments.show(this.fitmentId));
    } catch (e) {
      await this.fail(e);
      this.$emit("input", false);
    } finally {
      this.loading = false;
    }
  }

  /** Take what the server answered with as the state of the ticket. */
  private apply(fitment: EquipmentFitment): void {
    this.fitment = fitment;
    this.lines = fitment.lines.map(line => ({ ...line }));
    this.notes = fitment.notes;
    this.poNumber = fitment.poNumber || null;
    const dates = emptyDates();
    STAGE_KEYS.forEach(stage => {
      const row = this.stageOf(stage);
      dates[stage] = row ? row.stageDate : null;
    });
    this.dates = dates;
    const invoice = this.stageOf("invoice");
    this.invoice = {
      reference: invoice ? invoice.reference : null,
      total: invoice ? invoice.total : null
    };
    this.lines = this.lines.map(line => ({
      ...line,
      discount: line.discount || 0
    }));
    this.fieldErrors = {};
  }

  get readOnly(): boolean {
    return !this.fitment || this.fitment.status === "Closed";
  }

  get itemsLocked(): boolean {
    return !this.fitment || !!this.fitment.itemsLocked;
  }

  get canEditRevised(): boolean {
    return !!this.fitment && !!this.fitment.canEditRevised;
  }

  get canEditInvoice(): boolean {
    return !!this.fitment && !!this.fitment.canEditInvoice;
  }

  /** The proforma's date is free until it is sent; the others follow their columns. */
  dateEditable(stage: EquipmentFitmentStageKey): boolean {
    if (this.readOnly) return false;
    if (stage === "proforma") return (this.fitment && this.fitment.phase) === 0;
    return stage === "revised" ? this.canEditRevised : this.canEditInvoice;
  }

  canUpload(stage: EquipmentFitmentStageKey): boolean {
    const row = this.stageOf(stage);
    return !!row && !!row.canUpload;
  }

  /**
   * The proforma's and revised quote's value: the items' total until that
   * stage's document goes out, the figure that went out from then on.
   */
  derivedValue(stage: EquipmentFitmentStageKey): string {
    const row = this.stageOf(stage);
    const phase = (this.fitment && this.fitment.phase) || 0;
    const frozen =
      (stage === "proforma" && phase >= 1) ||
      (stage === "revised" && phase >= 2);
    if (frozen && row && row.total !== null)
      return Number(row.total).toFixed(2);
    return this.currentTotal.toFixed(2);
  }

  /**
   * Approve is shown while the ticket is pending and Finalize once it has
   * been approved, so there is only ever one next step on screen. A user who
   * holds neither permission sees neither button.
   */
  get showApprove(): boolean {
    return (
      !!this.fitment &&
      !this.isCashSale &&
      this.fitment.status === "Pending" &&
      !!this.fitment.mayApprove
    );
  }

  /**
   * A cash sale is paid on the spot and never approved, so Finalize is its
   * only next step and is offered from the start.
   */
  get showFinalize(): boolean {
    if (!this.fitment || !this.fitment.mayFinalize) return false;
    if (this.isCashSale) return this.fitment.status !== "Closed";
    return this.fitment.status === "Approved";
  }

  /** The server decides both of these; it knows the paperwork and the rights. */
  get canApprove(): boolean {
    return !!this.fitment && !!this.fitment.canApprove;
  }

  get canClose(): boolean {
    return !!this.fitment && !!this.fitment.canFinalize;
  }

  get scheduledAt(): string {
    if (!this.fitment || !this.fitment.scheduledDate) return "-";
    const date = moment(this.fitment.scheduledDate).format("DD/MM/YYYY");
    return this.fitment.scheduledTime
      ? `${date} ${this.fitment.scheduledTime}`
      : date;
  }

  /** What the items on screen come to, VAT included. */
  get currentTotal(): number {
    const subtotal =
      Math.round(
        this.lines.reduce((total, line) => {
          const unit =
            Math.round(
              Number(line.price) * (1 - Number(line.discount || 0) / 100) * 100
            ) / 100;
          return total + Math.round(Number(line.qty) * unit * 100) / 100;
        }, 0) * 100
      ) / 100;
    return (
      Math.round((subtotal + Math.round(subtotal * 0.15 * 100) / 100) * 100) /
      100
    );
  }

  /** Whether anything on screen differs from what was loaded. */
  get dirty(): boolean {
    if (!this.fitment) return false;
    const key = (lines: EquipmentFitmentLine[]) =>
      lines
        .map(
          line =>
            `${line.itemId}|${line.name}|${line.qty}|${
              line.price
            }|${line.discount || 0}`
        )
        .sort()
        .join(";");
    const loadedInvoice = this.stageOf("invoice");
    const stageDate = (stage: EquipmentFitmentStageKey) => {
      const row = this.stageOf(stage);
      return (row && row.stageDate) || "";
    };
    return (
      key(this.lines) !== key(this.fitment.lines) ||
      (this.notes || "") !== (this.fitment.notes || "") ||
      (this.poNumber || "") !== (this.fitment.poNumber || "") ||
      (this.invoice.reference || "") !==
        ((loadedInvoice && loadedInvoice.reference) || "") ||
      this.invoice.total !== (loadedInvoice ? loadedInvoice.total : null) ||
      STAGE_KEYS.some(stage => (this.dates[stage] || "") !== stageDate(stage))
    );
  }

  stageOf(stage: EquipmentFitmentStageKey): EquipmentFitmentStage | null {
    return (
      (this.fitment && this.fitment.stages && this.fitment.stages[stage]) ||
      null
    );
  }

  setInvoiceTotal(value: string): void {
    this.invoice.total =
      value === "" || value === null || Number.isNaN(Number(value))
        ? null
        : Math.round(Number(value) * 100) / 100;
    this.fieldErrors = {};
  }

  fieldErrorsFor(field: string): string[] {
    return this.fieldErrors[field] || [];
  }

  joined(
    first: string | null | undefined,
    second: string | null | undefined
  ): string {
    const parts = [first, second].filter(Boolean);
    return parts.length ? parts.join(" · ") : "-";
  }

  /** The word the server chose; the stored status is what drives the tabs. */
  get statusLabel(): string {
    if (!this.fitment) return "";
    if (this.fitment.displayStatus) return this.fitment.displayStatus;
    return this.fitment.status === "Closed"
      ? "Finalized"
      : this.fitment.status || "";
  }

  actionLabel(action: string): string {
    const key = action.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase()
    );
    return this.actionLabels[key] || action;
  }

  formatMoney(value: number): string {
    return `R ${Number(value || 0).toFixed(2)}`;
  }

  formatDate(value: string | null): string {
    return value ? moment(value).format("DD/MM/YYYY") : "";
  }

  formatDateTime(value: string | null): string {
    return value ? moment(value).format("DD/MM/YYYY HH:mm") : "-";
  }

  /**
   * The whole ticket: items, notes, order number and stages. Returns whether
   * it went through, so printing can save first and then print what is on
   * screen.
   */
  async save(quiet = false): Promise<boolean> {
    if (!this.fitment || !this.fitment.id) return false;
    this.busy = "save";
    try {
      this.apply(
        await EquipmentFitments.saveTicket(this.fitment.id, {
          lines: this.lines,
          notes: this.notes,
          poNumber: this.poNumber,
          stages: {
            proforma: { stageDate: this.dates.proforma },
            revised: { stageDate: this.dates.revised },
            invoice: {
              stageDate: this.dates.invoice,
              reference: this.invoice.reference,
              total: this.invoice.total
            }
          }
        })
      );
      this.changed = true;
      if (!quiet) {
        await this.$success("<strong>Success!</strong> Ticket saved.");
      }
      return true;
    } catch (e) {
      this.fieldErrors = this.fieldErrorsOf(e);
      await this.fail(e);
      return false;
    } finally {
      this.busy = null;
    }
  }

  /**
   * The quote as it stands. Unsaved changes are saved first so the print
   * shows what is on screen, not what was there before.
   */
  async downloadZip(): Promise<void> {
    if (!this.fitment || !this.fitment.id) return;
    this.busy = "zip";
    try {
      const file = await EquipmentFitments.downloadZip(this.fitment.id);
      const url = URL.createObjectURL(
        new Blob([file], { type: "application/zip" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `fitment-${this.fitment.id}-documents.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      await this.fail(e);
    } finally {
      this.busy = null;
    }
  }

  async printJobCard(): Promise<void> {
    if (!this.fitment || !this.fitment.id) return;
    if (this.dirty && !this.readOnly) {
      if (!(await this.save(true))) return;
    }
    this.busy = "print";
    try {
      const file = await EquipmentFitments.jobCard(this.fitment.id);
      window.open(
        URL.createObjectURL(new Blob([file], { type: "application/pdf" })),
        "_blank"
      );
    } catch (e) {
      await this.fail(e);
    } finally {
      this.busy = null;
    }
  }

  async approve(): Promise<void> {
    if (!this.fitment || !this.fitment.id) return;
    if (this.dirty && !(await this.save(true))) return;
    const answer = await this.$dialog.warning({
      text: `Approve fitment #${this.fitment.id} at revision ${
        this.fitment.revision
      } for ${this.formatMoney(this.fitment.totalPayable || 0)} incl. VAT?`,
      title: "Approve",
      persistent: false,
      actions: { No: { text: "No" }, Yes: { color: "primary", text: "Yes" } }
    });
    if (answer !== "Yes") return;

    this.busy = "approve";
    try {
      this.apply(await EquipmentFitments.approve(this.fitment.id));
      this.changed = true;
      await this.$success("<strong>Success!</strong> Fitment approved.");
    } catch (e) {
      await this.fail(e);
    } finally {
      this.busy = null;
    }
  }

  async closeTicket(): Promise<void> {
    if (!this.fitment || !this.fitment.id) return;
    if (this.dirty && !(await this.save(true))) return;
    const answer = await this.$dialog.error({
      text: `Finalize job #${this.fitment.id}? It moves to the Finalized tab and nothing on it can be changed afterwards.`,
      title: "Warning",
      persistent: false,
      actions: {
        No: { text: "No" },
        Yes: { color: "red", text: "Yes, finalize it" }
      }
    });
    if (answer !== "Yes") return;

    this.busy = "close";
    try {
      this.apply(await EquipmentFitments.close(this.fitment.id));
      this.changed = true;
      await this.$success("<strong>Success!</strong> Job finalized.");
    } catch (e) {
      await this.fail(e);
    } finally {
      this.busy = null;
    }
  }

  pickDocument(stage: EquipmentFitmentStageKey): void {
    const input = this.$refs["file-" + stage] as
      | HTMLInputElement[]
      | HTMLInputElement;
    const element = Array.isArray(input) ? input[0] : input;
    if (element) element.click();
  }

  async uploadDocument(
    stage: EquipmentFitmentStageKey,
    event: Event
  ): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = "";
    if (!file || !this.fitment || !this.fitment.id) return;

    this.busy = "upload-" + stage;
    try {
      this.apply(
        await EquipmentFitments.uploadStageDocument(
          this.fitment.id,
          stage,
          file
        )
      );
      this.changed = true;
      await this.$success("<strong>Success!</strong> Document uploaded.");
    } catch (e) {
      await this.fail(e);
    } finally {
      this.busy = null;
    }
  }

  async openDocument(stage: EquipmentFitmentStageKey): Promise<void> {
    if (!this.fitment || !this.fitment.id) return;
    this.busy = "doc-" + stage;
    try {
      const file = await EquipmentFitments.stageDocument(
        this.fitment.id,
        stage
      );
      window.open(URL.createObjectURL(file), "_blank");
    } catch (e) {
      await this.fail(e);
    } finally {
      this.busy = null;
    }
  }

  /**
   * The Close button, Esc or a click outside. The dialog is persistent so
   * that an unsaved ticket is not lost to a stray click: with changes on
   * screen it asks first.
   */
  async tryClose(): Promise<void> {
    if (this.busy) return;
    if (this.dirty && !this.readOnly) {
      const answer = await this.$dialog.warning({
        text: "Close without saving the changes on this ticket?",
        title: "Unsaved changes",
        persistent: false,
        actions: {
          No: { text: "Keep editing" },
          Yes: { color: "red", text: "Close without saving" }
        }
      });
      if (answer !== "Yes") return;
    }
    this.close();
  }

  close(): void {
    this.$emit("input", false);
    if (this.changed) {
      this.$emit("saved");
    }
    this.changed = false;
    this.fitment = null;
  }

  /** The field errors of a 422, keyed by field, or nothing. */
  private fieldErrorsOf(e: any): Record<string, string[]> {
    const errors = e && e.response && e.response.data && e.response.data.errors;
    return errors && typeof errors === "object" ? errors : {};
  }

  /**
   * Show what the server said: its field errors, its message, or failing
   * both the error itself.
   */
  private async fail(e: any): Promise<void> {
    const data = e && e.response && e.response.data;
    if (data && data.errors && typeof data.errors === "object") {
      await this.$error(data.errors);
      return;
    }
    const message =
      (data && data.result && data.result.message) ||
      (data && data.message) ||
      (e && e.message) ||
      "Something went wrong";
    await this.$error({ message });
  }
}
